import axios from 'axios'
import { clearToken, getStoredToken } from './authToken'

/**
 * Base HTTP client for the Vericast Events API.
 *
 * API base URL resolution, in priority order:
 *   1. VITE_API_BASE_URL (build-time) — REQUIRED for the Capacitor/native build,
 *      where the app is served from a WebView origin and `/api` is NOT the
 *      backend. Set to the absolute ingress API host (e.g. https://host/api).
 *   2. /config/config.json { apiUrl } — optional runtime override (web).
 *   3. same-origin `/api` — the web PWA default (ingress serves UI + API on one
 *      host).
 *
 * Auth: the web PWA uses the HttpOnly session cookie (withCredentials). The
 * native app has no cookie jar, so a request interceptor attaches the stored
 * attendee JWT as `Authorization: Bearer` when present (see services/authToken).
 */

const BUILD_API_BASE = import.meta.env.VITE_API_BASE_URL as string | undefined

let apiBaseUrl = BUILD_API_BASE || '/api'

// Runtime override only when a build-time absolute base wasn't provided.
if (!BUILD_API_BASE) {
  fetch('/config/config.json')
    .then((res) => res.json())
    .then((config) => {
      if (config.apiUrl) {
        apiBaseUrl = config.apiUrl
        apiClient.defaults.baseURL = config.apiUrl
      }
    })
    .catch(() => {
      // Use default if config fetch fails.
    })
}

export const apiClient = axios.create({
  baseURL: apiBaseUrl,
  timeout: 10000,
  // NOTE: do NOT set a default `Content-Type: application/json` here. axios adds
  // `application/json` per-request for JSON bodies automatically, but a hard
  // default *sticks* to FormData requests too — so multipart uploads went out as
  // `application/json` (no boundary), the server couldn't parse the parts, and
  // the `file` field was missing → 422. With no default, JSON still gets
  // application/json and FormData gets multipart/form-data (with boundary).
  withCredentials: true,
})

apiClient.interceptors.request.use((config) => {
  // Belt-and-braces: if a FormData body somehow carries an explicit Content-Type,
  // strip it so the browser sets multipart/form-data with the required boundary.
  const isFormData =
    typeof FormData !== 'undefined' && config.data instanceof FormData
  if (isFormData && config.headers) {
    // AxiosHeaders in v1 exposes delete(); fall back to property delete.
    const h = config.headers as unknown as {
      delete?: (name: string) => void
      [k: string]: unknown
    }
    if (typeof h.delete === 'function') h.delete('Content-Type')
    else delete h['Content-Type']
  }

  // Attach the bearer token on native (no-op on web — the web PWA uses the
  // session cookie; getStoredToken() returns null there).
  const token = getStoredToken()
  if (token) {
    config.headers = config.headers ?? {}
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

/**
 * Attendee-session expiry handling.
 *
 * When a session cookie/token is no longer valid (e.g. the API's
 * ATTENDEE_SESSION_SECRET rotated on a rebuild, or the TTL lapsed), auth-gated
 * endpoints return 401. Rather than let each page surface a cryptic error (or
 * crash on a null payload), we clear any stale bearer token and notify the auth
 * context so it re-checks /attendee/me and flips the UI to a clean signed-out
 * state. A callback is registered by AttendeeAuthContext to avoid a circular
 * import.
 */
let onAuthExpired: (() => void) | null = null
export function setOnAuthExpired(cb: (() => void) | null): void {
  onAuthExpired = cb
}

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status
    const url: string = error?.config?.url ?? ''
    // Only react to attendee-scoped 401s; never intercept the auth probe itself
    // (/attendee/me legitimately returns for signed-out users) to avoid loops.
    if (status === 401 && !url.includes('/attendee/me')) {
      clearToken()
      onAuthExpired?.()
    }
    return Promise.reject(error)
  },
)

/** Absolute API base URL (for building raw fetch/SSE/asset URLs). */
export function apiBaseURL(): string {
  return apiClient.defaults.baseURL ?? apiBaseUrl
}

export default apiClient
